import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, TrendingUp } from "lucide-react";
import { VoteButton } from "./VoteButton";
import { useNavigate } from "react-router-dom";

interface LeaderboardItem {
  listing_id: string;
  listing_name: string;
  listing_slug: string;
  listing_address?: string;
  listing_thumbnail?: string;
  listing_rating?: number;
  all_time_votes: number;
  rank: number;
}

interface NearMissGridProps {
  items: LeaderboardItem[];
  citySlug: string;
  activitySlug: string;
}

export const NearMissGrid = ({ items, citySlug, activitySlug }: NearMissGridProps) => {
  const navigate = useNavigate();
  
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <TrendingUp className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold">Casi en el Top</h2>
        <Badge variant="secondary">Posiciones 4-9</Badge>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <Card 
            key={item.listing_id}
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] overflow-hidden"
            onClick={() => navigate(`/listing/${item.listing_slug}`)}
          >
            {/* Thumbnail Image - ALWAYS SHOW with fallback */}
            <div className="relative h-40 w-full overflow-hidden bg-muted">
              <img
                src={item.listing_thumbnail || '/default-listing-fallback.png'}
                alt={item.listing_name}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.currentTarget.src = '/default-listing-fallback.png';
                }}
              />
              <div className="absolute top-2 left-2">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shadow-lg">
                  #{item.rank}
                </div>
              </div>
            </div>
            
            <CardContent className="p-4">
              <div className="space-y-3">
                <h3 className="font-semibold text-base line-clamp-2">
                  {item.listing_name}
                </h3>
                
                {item.listing_address && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="line-clamp-1">{item.listing_address}</span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-2">
                  <Badge variant="outline" className="text-xs">
                    {item.all_time_votes} votos
                  </Badge>
                  
                  <div onClick={(e) => e.stopPropagation()}>
                    <VoteButton
                      listingId={item.listing_id}
                      citySlug={citySlug}
                      activitySlug={activitySlug}
                      currentVotes={item.all_time_votes}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
